'use client';

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Box,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { apiClient } from '@/services/api';

interface WechatBindDialogProps {
  open: boolean;
  onClose: () => void;
}

const WechatBindDialog: React.FC<WechatBindDialogProps> = ({ open, onClose }) => {
  const [status, setStatus] = useState<'checking' | 'bound' | 'unbound' | 'binding' | 'polling' | 'success' | 'error' | 'timeout'>('checking');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [error, setError] = useState('');
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [bindStatus, setBindStatus] = useState<'bound' | 'unbound' | null>(null);
  const [unbindLoading, setUnbindLoading] = useState(false);

  // 检查绑定状态
  const checkBindStatus = async () => {
    try {
      setStatus('checking');
      const response = await apiClient.get('/auth/wechat/check-bind-status');
      
      if (response.success) {
        setBindStatus(response.data?.isBound ? 'bound' : 'unbound');
        setStatus(response.data?.isBound ? 'bound' : 'unbound');
        
        // 如果未绑定，自动生成二维码
        if (!response.data?.isBound) {
          setTimeout(() => {
            generateQR();
          }, 500); // 延迟500ms让用户看到状态变化
        }
      } else {
        setError('检查绑定状态失败');
        setBindStatus('unbound');
        setStatus('unbound');
        // 即使检查失败，也尝试生成二维码
        setTimeout(() => {
          generateQR();
        }, 500);
      }
    } catch (error) {
      setError('检查绑定状态失败');
      setBindStatus('unbound');
      setStatus('unbound');
      // 即使检查失败，也尝试生成二维码
      setTimeout(() => {
        generateQR();
      }, 500);
    }
  };

  // 解除绑定
  const handleUnbind = async () => {
    if (!confirm('确定要解除微信绑定吗？解除后将无法使用微信扫码登录。')) {
      return;
    }

    setUnbindLoading(true);
    try {
      const response = await apiClient.post('/auth/wechat/unbind');

      if (response.success) {
        setBindStatus('unbound');
        setStatus('unbound');
        setQrCodeUrl('');
        setError('');
      } else {
        setError(response.message || '解除绑定失败');
      }
    } catch (error) {
      setError('解除绑定失败');
    } finally {
      setUnbindLoading(false);
    }
  };

  // 生成绑定二维码
  const generateQR = async () => {
    setStatus('binding');
    setError('');
    
    try {
      const response = await apiClient.post('/auth/wechat/bind', {
        type: 'bind'
      });

      if (response.success && response.data?.qrCodeUrl) {
        setQrCodeUrl(response.data.qrCodeUrl);
        setStatus('polling');
        
        // 开始轮询检查状态
        startPolling(response.data.sessionId);
      } else {
        throw new Error(response.message || '生成绑定二维码失败');
      }
      
    } catch (error) {
      setError('生成绑定二维码失败');
      setStatus('error');
    }
  };

  // 轮询检查绑定状态
  const startPolling = (sessionId: string) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const response = await apiClient.get(`/auth/wechat/check-status?sessionId=${sessionId}`);

        if (response.success) {
          if (response.data?.status === 'success') {
            setStatus('success');
            setBindStatus('bound');
            clearInterval(interval);
            setPollingInterval(null);
            
            // 3秒后自动关闭对话框
            setTimeout(() => {
              onClose();
            }, 3000);
          } else if (response.data?.status === 'failed') {
            setStatus('error');
            setError('绑定失败，请重试');
            clearInterval(interval);
            setPollingInterval(null);
          }
        }
      } catch (error) {
      }
    }, 2000); // 每2秒检查一次

    setPollingInterval(interval);

    // 30秒后停止轮询
    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPollingInterval(null);
        // 检查当前状态是否为轮询状态
        setStatus(prevStatus => {
          if (prevStatus === 'polling') {
            setError('绑定超时，请重新生成二维码');
            return 'timeout';
          }
          return prevStatus;
        });
      }
    }, 30000);
  };

  // 清理轮询
  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // 重置状态
  const resetState = () => {
    setStatus('checking');
    setQrCodeUrl('');
    setError('');
    setBindStatus(null);
    stopPolling();
  };

  // 对话框打开时检查绑定状态
  useEffect(() => {
    if (open) {
      resetState();
      checkBindStatus();
    } else {
      stopPolling();
    }
  }, [open]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const handleClose = () => {
    stopPolling();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 400
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          微信账号绑定
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {status === 'checking' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              正在检查绑定状态...
            </Typography>
          </Box>
        )}

        {status === 'bound' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" sx={{ color: 'success.main', mb: 2 }}>
              ✅ 已绑定微信账号
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              绑定成功后，可使用微信扫码快速登录
            </Typography>
            <Button 
              variant="outlined" 
              color="error"
              onClick={handleUnbind}
              disabled={unbindLoading}
              sx={{ mr: 2 }}
            >
              {unbindLoading ? <CircularProgress size={20} /> : '解除绑定'}
            </Button>
            <Button variant="contained" onClick={handleClose}>
              关闭
            </Button>
          </Box>
        )}

        {(status === 'unbound' || status === 'binding' || status === 'polling' || status === 'error' || status === 'timeout' || status === 'success') && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: 'primary.main' }}>
              微信账号绑定
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                第一步: 使用微信扫描下方二维码，关注我们的公众号
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                第二步: 关注成功后，再次扫描二维码即可完成账号绑定
              </Typography>
            </Box>

            {status === 'binding' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  正在生成二维码...
                </Typography>
              </Box>
            )}

            {status === 'polling' && qrCodeUrl && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Box 
                  component="img" 
                  src={qrCodeUrl} 
                  alt="微信绑定二维码"
                  sx={{ 
                    maxWidth: 200, 
                    maxHeight: 200,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1
                  }}
                />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  请使用微信扫描上方二维码
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                  二维码有效期为5分钟
                </Typography>
              </Box>
            )}

            {status === 'success' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ color: 'success.main', mb: 2 }}>
                  ✅ 绑定成功！
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  绑定成功后，可使用微信扫码快速登录
                </Typography>
              </Box>
            )}

            {(status === 'error' || status === 'timeout') && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" sx={{ color: 'error.main', mb: 2 }}>
                  {status === 'timeout' ? '绑定超时，请重新生成二维码' : '绑定失败，请重试'}
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={generateQR}
                  sx={{ 
                    bgcolor: '#07C160',
                    '&:hover': { bgcolor: '#06AD56' }
                  }}
                >
                  重新生成二维码
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          关闭
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WechatBindDialog;